import { Form, Formik } from 'formik';
import { useRef } from 'react';
import DocumentTitle from 'react-document-title';
import { useLocation } from 'react-router-dom';

import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';

import CreateEditButtons from 'hb-react/frontoffice/apps/Forum/components/CreateEditButtons';
import CreateEditInputs from 'hb-react/frontoffice/apps/Forum/containers/CreateEditInputs';
import { useForumContext } from 'hb-react/frontoffice/apps/Forum/contexts/ForumContext';
import { type DiscussionEditValues } from 'hb-react/frontoffice/apps/Forum/requests/requests.types';
import {
  type Discussion,
  type FormValues,
} from 'hb-react/frontoffice/apps/Forum/types';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { type QuillEditorV3Ref } from 'hb-react/shared/components/DesignSystem/QuillEditor/types';
import RequiredExplanation from 'hb-react/shared/components/formik/components/RequiredExplanation';
import { LockScreen } from 'hb-react/shared/components/LockScreen';
import { usePageMenuName, PAGES } from 'hb-react/shared/hooks/usePageMenuName';
import { generatePageTitle } from 'hb-react/shared/utils/generatePageTitle';
import { getPageTitleFromNetwork } from 'hb-react/shared/utils/getPageTitleFromNetwork';

interface CreateEditFormProps {
  coverPictureUrl?: Discussion['cover_picture_url'];
  initialValues: FormValues;
  mutate: (values: DiscussionEditValues) => Promise<Partial<Discussion>>;
  showUserTargetingButton: boolean;
  title: string;
}

const CreateEditForm = ({
  coverPictureUrl,
  initialValues,
  mutate,
  showUserTargetingButton,
  title,
}: CreateEditFormProps) => {
  const quillEditorRef = useRef<QuillEditorV3Ref | null>(null);
  const { currentNetwork } = useDefaultAppContexts();
  const { topicName } = useForumContext();
  const { pathname } = useLocation();

  const handleSubmit = async (values: FormValues) => {
    const data = {
      content: values.content,
      title: values.title,
      storage_attachments: values.storage_attachments,
      storage_uploads: values.storage_uploads,
      storage_attachments_attachments_attributes:
        values.storage_attachments_attachments_attributes,
      storage_uploads_attachments_attributes:
        values.storage_uploads_attachments_attributes,
      category_ids: values.category_ids.map((id) => id.toString()),
      cover_picture: values.cover_picture,
      ...(values.targeted_user_ids && {
        targeted_user_ids: values.targeted_user_ids,
      }),
    };

    await mutate(data as DiscussionEditValues);
  };

  const defaultPageTitle = s__('PageTitle|Forum');
  const menuName = usePageMenuName(PAGES.FORUM, defaultPageTitle);
  const getTitle = () => {
    const isNew = /\/new$/.test(pathname);

    if (isNew) {
      return `${s__('PageTitle|New')} ${menuName}`;
    }

    return `${s__('PageTitle|Edit')} ${
      initialValues.title || defaultPageTitle
    }`;
  };

  return (
    <>
      <DocumentTitle
        title={generatePageTitle({
          title: getTitle(),
          networkName: getPageTitleFromNetwork(currentNetwork),
          topicName,
        })}
      />
      <Card pb={{ _: 6, m: 8 }} noBorderRadiusOnSmallScreen>
        <Box as="h1" font="h3-semibold" display="block">
          {title}
        </Box>
        <Formik initialValues={initialValues} onSubmit={handleSubmit}>
          {({ isSubmitting }) => (
            <>
              {isSubmitting && <LockScreen />}
              <Form>
                <RequiredExplanation mt={2} mb={4} />
                <CreateEditInputs
                  coverPictureUrl={coverPictureUrl}
                  quillEditorRef={quillEditorRef}
                />
                <CreateEditButtons
                  isUploading={isSubmitting}
                  showUserTargetingButton={showUserTargetingButton}
                />
              </Form>
            </>
          )}
        </Formik>
      </Card>
    </>
  );
};

export default CreateEditForm;
